import React from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import { LANDING } from "@/constants/testIds";

const FOOTER_NAV = [
  {
    title: "Platform",
    links: [
      { label: "Overview", href: "/#platform" },
      { label: "Strategies", to: "/strategies" },
      { label: "Capital Pathways", to: "/capital-pathways", testId: LANDING.footerCapitalPathways },
      { label: "Fund Architecture", to: "/funds", testId: LANDING.footerFunds },
      { label: "Why Now", href: "/#why-now" },
      { label: "Downside Protection", href: "/#downside-protection" },
    ],
  },
  {
    title: "Insight",
    links: [
      { label: "Perspectives", to: "/perspectives" },
      { label: "Newsroom", to: "/news", testId: LANDING.footerNews },
      { label: "Media Enquiries", to: "/contact" },
    ],
  },
  {
    title: "Firm",
    links: [
      { label: "The Partnership", to: "/partnership" },
      { label: "Team", to: "/team", testId: LANDING.footerTeam },
      { label: "Responsible Capital", to: "/responsible-capital", testId: LANDING.footerResponsible },
      { label: "Careers", to: "/careers", testId: LANDING.footerCareers },
      { label: "Press Kit", to: "/press-kit", testId: LANDING.footerPressKit },
    ],
  },
  {
    title: "Engage",
    links: [
      { label: "Investor Access", href: "/#investor-access" },
      { label: "Strategic Partnership", href: "/#investor-access" },
      { label: "For Gulf Investors", to: "/gulf-investors", testId: "kr-footer-gulf-investors" },
      { label: "General Enquiry", to: "/contact", testId: LANDING.footerContact },
      { label: "Disclosures", to: "/disclosures", testId: LANDING.footerDisclosures },
    ],
  },
];

const LEGAL_NAV = [
  { label: "Legal Notices", to: "/legal", testId: LANDING.footerLegal },
  { label: "Privacy", to: "/privacy", testId: LANDING.footerPrivacy },
  { label: "Cookies", to: "/cookies", testId: LANDING.footerCookies },
];

const Footer = () => {
  return (
    <footer
      data-testid={LANDING.footer}
      className="relative pt-24 pb-12 border-t border-[var(--kr-line)]"
      style={{ background: "#070a0f" }}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <Logo size="small" testId={LANDING.footerLogo} />
            <p className="mt-8 text-[var(--kr-text-muted)] text-[14.5px] leading-[1.8] max-w-[44ch]">
              Kellermann Reid &amp; Co. is a private markets platform in
              strategic formation. Materials on this site are informational and
              do not constitute an offer, solicitation or recommendation.
            </p>
            <a
              href="mailto:ir@krprivatecapital.com"
              className="mt-6 inline-block kr-link font-serif-display text-[1.1rem]"
            >
              ir@krprivatecapital.com
            </a>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {FOOTER_NAV.map((col) => (
              <div key={col.title}>
                <div className="font-mono-label text-[10px] text-[var(--kr-gold)] mb-6">
                  {col.title}
                </div>
                <ul className="space-y-3">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.to ? (
                        <Link
                          to={l.to}
                          data-testid={l.testId}
                          className="text-[13.5px] text-[var(--kr-text)]/85 hover:text-[var(--kr-gold)] transition-colors"
                        >
                          {l.label}
                        </Link>
                      ) : (
                        <a
                          href={l.href}
                          className="text-[13.5px] text-[var(--kr-text)]/85 hover:text-[var(--kr-gold)] transition-colors"
                        >
                          {l.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="kr-rule mb-8" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 font-mono-label text-[10px] text-[var(--kr-text-dim)]">
          <div>© {new Date().getFullYear()} Kellermann Reid &amp; Co. — All rights reserved.</div>
          <div className="flex flex-wrap gap-6 items-center">
            {LEGAL_NAV.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className="hover:text-[var(--kr-gold)] transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <span className="hidden md:inline w-px h-3 bg-[var(--kr-line)]" />
            <span>Confidential · By Introduction</span>
          </div>
        </div>

        <p className="mt-10 text-[11.5px] leading-[1.7] text-[var(--kr-text-muted)] max-w-[120ch]">
          This site is directed only to eligible investors in jurisdictions where lawful.{" "}
          <Link
            to="/disclosures#correspondence"
            data-testid={LANDING.footerCorrespondence}
            className="kr-link"
          >
            Marketing Restrictions &amp; Jurisdictional Correspondence →
          </Link>
        </p>

        <p className="mt-4 text-[12px] leading-[1.7] text-[var(--kr-text-dim)] max-w-[120ch]">
          Important Notice — This website is provided for general information
          purposes only and is not directed at, intended for, or available to,
          any person in any jurisdiction where the publication or availability
          would be contrary to local law or regulation. Nothing on this site
          constitutes an offer to sell, or a solicitation of an offer to
          subscribe for or buy, any security, fund interest or financial
          instrument. Any reference to the platform&apos;s intended strategies,
          mandates or governance is preparatory in nature and subject to
          documentation, regulatory authorisation and onboarding of eligible,
          sophisticated, professional or institutional investors.
        </p>

        {/* Brand seal — full wordmark + tagline */}
        <div
          data-testid={LANDING.footerBrandSeal}
          className="mt-20 pt-12 border-t border-[var(--kr-line)] flex flex-col items-center"
        >
          <Link
            to="/"
            aria-label="Kellermann Reid & Co. — Home"
            className="group inline-block"
          >
            <img
              src="/brand/kr-logo-cream-small.png"
              alt="Kellermann Reid & Co. — Private Markets Investment House"
              className="w-[280px] md:w-[360px] opacity-90 group-hover:opacity-100 transition-opacity duration-700"
              loading="lazy"
              decoding="async"
            />
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
